/**
 * 完了通知タブ（2026-06-18）。
 * フォーム送信完了時に、回答者本人のメールアドレス宛へ「回答内容の控え」を自動送信する設定画面。
 * 本文構成: 01 本文 → 02 URL（空ならスキップ）→ 回答 → フッター（会社情報・自動付与）。
 * 完了通知 ON のフォームでは、既存の form_submitted 業務サンクスメールは送られない（二重送信防止）。
 */
import { Save } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { EmailChipsInput } from '@/components/common/EmailChipsInput';
import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useForm, useUpdateForm } from '@/hooks/useForms';
import { useMailAccounts } from '@/hooks/useMailAccounts';

import { FormPageTabs } from './FormPageTabs';

import {
  buildCompletionMailText,
  type FieldFormElement,
  type FormCompletionNotification,
} from '@goenhub/shared';

const DEFAULT_CFG: FormCompletionNotification = {
  enabled: false,
  fromAccountId: null,
  cc: [],
  subject: '',
  body: '',
  url: null,
  includedFieldIds: null,
};

export function FormCompletionNotificationPage() {
  const { id } = useParams<{ id: string }>();
  const { data: original, isLoading } = useForm(id);
  const updateMutation = useUpdateForm();
  const { data: mailAccounts } = useMailAccounts();

  const [cfg, setCfg] = useState<FormCompletionNotification>(DEFAULT_CFG);
  const [dirty, setDirty] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (original && !loaded) {
      setCfg(original.completionNotification ?? DEFAULT_CFG);
      setLoaded(true);
    }
  }, [original, loaded]);

  const activeAccounts = useMemo(
    () => (mailAccounts ?? []).filter((a) => a.status === 'active'),
    [mailAccounts],
  );

  // 回答対象に選べる質問フィールド一覧
  const fields = useMemo<FieldFormElement[]>(
    () =>
      ((original?.elements ?? []).filter(
        (e) => e.kind === 'field',
      ) as FieldFormElement[]),
    [original],
  );
  const allFieldIds = useMemo(() => fields.map((f) => f.id), [fields]);

  // includedFieldIds が null/undefined は「全フィールド」。チェック判定用に展開する。
  const isFieldIncluded = (fieldId: string) => {
    const ids = cfg.includedFieldIds;
    return ids == null || ids.includes(fieldId);
  };

  const patch = (next: Partial<FormCompletionNotification>) => {
    setCfg((prev) => ({ ...prev, ...next }));
    setDirty(true);
  };

  const toggleField = (fieldId: string) => {
    const effective = new Set(
      cfg.includedFieldIds == null ? allFieldIds : cfg.includedFieldIds,
    );
    if (effective.has(fieldId)) effective.delete(fieldId);
    else effective.add(fieldId);
    // 全選択なら null（＝今後追加する質問も自動的に含む）に正規化
    const allSelected = allFieldIds.every((fid) => effective.has(fid));
    patch({
      includedFieldIds: allSelected
        ? null
        : allFieldIds.filter((fid) => effective.has(fid)),
    });
  };

  const handleSave = async () => {
    if (!id) return;
    if (cfg.enabled && !cfg.subject.trim()) {
      toast.error('件名を入力してください');
      return;
    }
    if (cfg.enabled && !cfg.body.trim()) {
      toast.error('本文を入力してください');
      return;
    }
    try {
      const toSave: FormCompletionNotification = {
        enabled: cfg.enabled,
        fromAccountId: (cfg.fromAccountId ?? '') || null,
        cc: (cfg.cc ?? []).map((c) => c.trim().toLowerCase()).filter(Boolean),
        subject: cfg.subject.trim(),
        body: cfg.body,
        url: (cfg.url ?? '').trim() || null,
        includedFieldIds: cfg.includedFieldIds,
      };
      await updateMutation.mutateAsync({
        id,
        data: { completionNotification: toSave },
      });
      toast.success('保存しました');
      setDirty(false);
    } catch (err) {
      toast.error(`保存失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // プレビュー本文（回答は placeholder で代替表示。フッターは送信時に自動付与）
  const previewText = useMemo(() => {
    const includedFields = fields.filter((f) => isFieldIncluded(f.id));
    const answers = includedFields.map((f) => ({
      label: f.label || '(無題の質問)',
      value:
        f.fieldType === 'consent'
          ? '同意しました'
          : f.placeholder || '（回答例）',
    }));
    return buildCompletionMailText({
      body: cfg.body,
      url: cfg.url ?? '',
      answers,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.body, cfg.url, cfg.includedFieldIds, fields]);

  if (isLoading || !original) {
    return (
      <Screen title="完了通知" backTo="/campaigns?hub=forms">
        <div className="flex justify-center py-20">
          <Spinner />
        </div>
      </Screen>
    );
  }

  return (
    <Screen
      title={`完了通知 — ${original.name}`}
      description="フォーム回答者本人へ、回答内容の控えメールを自動送信します。"
      backTo="/campaigns?hub=forms"
      actions={
        <Button onClick={handleSave} disabled={!dirty || updateMutation.isPending}>
          <Save className="size-4" />
          {updateMutation.isPending ? '保存中…' : '保存'}
        </Button>
      }
    >
      {id && (
        <FormPageTabs
          formId={id}
          submissionCount={original.submissionCount ?? 0}
          active="completion"
          dirty={dirty}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* === 左：設定 === */}
        <div className="space-y-5">
          {/* ON/OFF */}
          <label className="flex items-center gap-3 px-4 py-3 rounded border border-[var(--border)] bg-[var(--bg-elevated)] cursor-pointer">
            <input
              type="checkbox"
              checked={cfg.enabled}
              onChange={(e) => patch({ enabled: e.target.checked })}
              className="size-4"
            />
            <div>
              <div className="text-[13px] font-medium">完了通知を送信する</div>
              <div className="text-[11px] text-[var(--text-muted)]">
                ON の間、フォーム送信完了時に回答者のメールアドレスへ控えを送ります（既存のサンクスメールは抑制されます）。
              </div>
            </div>
          </label>

          {/* 送信元 */}
          <div>
            <Label className="text-xs">送信元アドレス</Label>
            <Select
              value={cfg.fromAccountId ?? '__default__'}
              onValueChange={(v) =>
                patch({ fromAccountId: v === '__default__' ? null : v })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__default__">
                  システム既定（自動選択）
                </SelectItem>
                {activeAccounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.displayName}（{a.email}）{a.isDefault ? ' ★既定' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* CC */}
          <div>
            <Label className="text-xs">CC（任意）</Label>
            <EmailChipsInput
              value={cfg.cc ?? []}
              onChange={(next) => patch({ cc: next })}
              placeholder="控えを受け取るメアド（自分・社内など）"
            />
            <p className="mt-1 text-[11px] text-[var(--text-muted)]">
              自分のメールアドレスや、システムに登録しているアドレスを入力できます（Enter / カンマで確定）。
            </p>
          </div>

          {/* 件名 */}
          <div>
            <Label className="text-xs">件名</Label>
            <Input
              value={cfg.subject}
              onChange={(e) => patch({ subject: e.target.value })}
              placeholder="例: 【カウデザイン】お申し込みありがとうございます"
            />
          </div>

          {/* 本文（01） */}
          <div>
            <Label className="text-xs">本文（01）</Label>
            <Textarea
              value={cfg.body}
              onChange={(e) => patch({ body: e.target.value })}
              rows={6}
              placeholder={
                'この度はお申し込みいただきありがとうございます。\n以下の内容で受け付けました。'
              }
            />
          </div>

          {/* URL（02） */}
          <div>
            <Label className="text-xs">URL リンク（02・任意）</Label>
            <Input
              value={cfg.url ?? ''}
              onChange={(e) => patch({ url: e.target.value })}
              placeholder="https://example.com/（無ければ空欄でOK）"
            />
            <p className="mt-1 text-[11px] text-[var(--text-muted)]">
              空欄のときは本文生成時にスキップされます。
            </p>
          </div>

          {/* 表示する回答 */}
          <div>
            <Label className="text-xs">本文に載せる回答（複数選択可）</Label>
            <div className="mt-1 rounded border border-[var(--border)] divide-y divide-[var(--border)]">
              {fields.length === 0 && (
                <div className="px-3 py-2 text-[12px] text-[var(--text-muted)]">
                  質問フィールドがありません（ビルダーで追加してください）。
                </div>
              )}
              {fields.map((f) => (
                <label
                  key={f.id}
                  className="flex items-center gap-2 px-3 py-2 text-[13px] cursor-pointer hover:bg-[var(--bg-elevated)]"
                >
                  <input
                    type="checkbox"
                    checked={isFieldIncluded(f.id)}
                    onChange={() => toggleField(f.id)}
                    className="size-3.5"
                  />
                  <span className="truncate">{f.label || '(無題の質問)'}</span>
                  <span className="ml-auto text-[10px] text-[var(--text-muted)]">
                    {f.fieldType}
                  </span>
                </label>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-[var(--text-muted)]">
              既定では全項目が選択されています。空の回答は自動で省略されます。
            </p>
          </div>
        </div>

        {/* === 右：プレビュー === */}
        <div className="lg:sticky lg:top-4">
          <div className="text-[11px] text-[var(--text-muted)] mb-2">
            プレビュー（回答は例示です。実際の送信では回答者の入力値が入ります）
          </div>
          <div className="rounded border border-[var(--border)] bg-[var(--bg-surface)] p-4">
            <div className="text-[12px] text-[var(--text-muted)] mb-1">
              件名: <span className="text-[var(--text-primary)]">{cfg.subject || '（未入力）'}</span>
            </div>
            <hr className="my-2 border-[var(--border)]" />
            <pre className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-[var(--text-primary)] font-sans">
              {previewText || '（本文が未入力です）'}
            </pre>
            <hr className="my-2 border-[var(--border)]" />
            <p className="text-[11px] text-[var(--text-muted)]">
              ※ 末尾に会社情報フッター（システム＞一般設定の会社情報）が自動付与されます。
            </p>
          </div>
        </div>
      </div>
    </Screen>
  );
}
